/**
 * Pin-first-product (Den Liu / WeScale "Pin the Clicked Product" approach).
 *
 * When an ad deep-links to a collection page with `?first=<product-handle>`,
 * the clicked product is shown as the FIRST card in the grid - replicating
 * Facebook Shop's behavior where the tapped catalog item is always in the #1
 * position. For Meta catalog ads, enable "Override catalog website deep links"
 * and template the URL with the catalog's Content ID (= Shopify variant id;
 * Meta has no handle field):
 *   https://tinygainsclub.com/collections/sale?first={{product.retailer_id}}
 * A numeric `first` is looked up via Shopify's /variants/<id>.js, so the
 * pinned card shows and links to that exact variant (the colour that was in
 * the ad). Static ads can still pass a plain handle.
 *
 * The lookups run in an inline
 * script in sections/main-collection.liquid, which also hides the grid until
 * this module has pinned the card, so shoppers never see the swap.
 *
 * Two paths:
 * 1. Product is already in the server-rendered grid -> move its <li> to the
 *    front. Perfect fidelity.
 * 2. Product is on a later page (common: Sale has 370+ products, 24/page) ->
 *    clone the grid's first card as a styling
 *    template, and swap in the pinned product's image/title/price/links.
 *    Variant swatches and quick-add are stripped from the clone since they
 *    would still reference the template card's product.
 *
 * Infinite scroll can later append the pinned product's original card
 * (duplicate); a MutationObserver removes the non-pinned duplicate.
 */

const params = new URLSearchParams(window.location.search);
const pinnedParam = params.get('first');
const variantId = /^\d+$/.test(pinnedParam || '') ? pinnedParam : null;

/** Points a moved card's product links at the variant (colour) from the ad. */
function linkToVariant(li) {
  if (!variantId) return;
  for (const a of li.querySelectorAll('a[href*="/products/"]')) {
    a.href = `${a.href.split('?')[0]}?variant=${variantId}`;
  }
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch (e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

/**
 * Removes any later-loaded (infinite scroll) copy of the pinned product so it
 * never appears twice in the grid.
 * @param {HTMLElement} grid
 * @param {string} productId
 */
function watchForDuplicates(grid, productId) {
  if (!productId) return;
  new MutationObserver(() => {
    for (const li of grid.querySelectorAll(`li.product-grid__item[data-product-id="${productId}"]`)) {
      if (!li.hasAttribute('data-pinned-first')) li.remove();
    }
  }).observe(grid, { childList: true });
}


/**
 * Builds a lookalike card for a product that isn't in the rendered grid, by
 * cloning the first card (so all theme styling carries over) and swapping the
 * product-specific parts.
 * @param {HTMLElement} grid
 * @param {object} product - response of /products/<handle>.js
 */
function buildPinnedCard(grid, product) {
  const template = grid.querySelector('li.product-grid__item');
  if (!template) return null;

  const li = /** @type {HTMLLIElement} */ (template.cloneNode(true));
  li.setAttribute('data-pinned-first', 'true');
  li.dataset.productId = String(product.id);
  li.removeAttribute('id');

  // These would still act on the template card's product - remove them.
  for (const el of li.querySelectorAll(
    'swatches-variant-picker-component, .quick-add, slideshow-arrows, slideshow-controls, .product-card__color-label'
  )) {
    el.remove();
  }

  // Keep only the first media slide so the hover image swap can't show the
  // template product.
  li.querySelectorAll('slideshow-slide').forEach((slide, index) => {
    if (index > 0) slide.remove();
  });

  for (const a of li.querySelectorAll('a')) a.href = product.url;

  const card = li.querySelector('product-card');
  if (card) card.setAttribute('data-product-id', String(product.id));

  const img = li.querySelector('img');
  if (img && product.featured_image) {
    const base = product.featured_image;
    img.src = base + (base.includes('?') ? '&' : '?') + 'width=800';
    img.removeAttribute('srcset');
    img.alt = product.title;
  }

  const titleEl = [...li.querySelectorAll('.text-block p, .text-block')].find(
    (el) => el.textContent.trim().length > 0
  );
  if (titleEl) titleEl.textContent = product.title;

  const priceEl = li.querySelector('product-price');
  if (priceEl) {
    const parts = [formatMoney(product.price)];
    if (product.compare_at_price && product.compare_at_price > product.price) {
      parts.push(formatMoney(product.compare_at_price));
    }
    priceEl.textContent = parts.join(' ');
  }

  return li;
}

/**
 * @param {object | null} product - {id, title, url, price, compare_at_price, featured_image}
 *   (a /products/<handle>.js response, or the same shape built from a variant), or null
 */
function pin(product) {
  const grid = document.querySelector('li.product-grid__item')?.parentElement;
  if (!grid || !product) return;

  const existing = grid.querySelector(`li.product-grid__item[data-product-id="${product.id}"]`);
  const li = existing || buildPinnedCard(grid, product);
  if (!li) return;
  li.setAttribute('data-pinned-first', 'true');
  if (existing) linkToVariant(li);
  grid.prepend(li);
  // A card cloned from a later page made page 1 one longer than the theme's page size, leaving one
  // card alone on a new row (owner, 2026-10-14). Drop the last card so the page keeps its size.
  // ponytail: that product is skipped for this visitor (page 2 links don't carry ?first=);
  // carry it over to page 2 if that ever matters.
  if (!existing) [...grid.querySelectorAll('li.product-grid__item:not([data-pinned-first])')].pop()?.remove();
  watchForDuplicates(grid, String(product.id));
}

// The lookup was started by the inline script in main-collection.liquid while the
// page was still loading; the grid stays hidden (html.tgc-pinning) until it's pinned.
if (pinnedParam && window.tgcPinFirst) {
  window.tgcPinFirst
    .then(pin)
    .catch(() => {})
    .finally(() => document.documentElement.classList.remove('tgc-pinning'));
}
